// src/chatbot/components/AudioPlayer.tsx
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaPlay, FaPause } from "react-icons/fa";
import type { Branch } from "~/types/new_types";

interface AudioSource {
  src: string;
  type: string;
}

interface AudioPlayerProps {
  audioSource: AudioSource;
  theme: Branch["theme"];
  isUser: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioSource,
  theme,
  isUser,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Format time helper
  const formatAudioTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Smooth progress update using requestAnimationFrame
  useEffect(() => {
    const updateProgress = () => {
      if (audioRef.current && isPlaying) {
        setPlaybackTime(audioRef.current.currentTime);
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying]);

  // Reset audio when source changes
  useEffect(() => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setPlaybackTime(0);
    setDuration(0);
    audioRef.current.load();
  }, [audioSource]);

  // Handlers
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setPlaybackTime(0);
  };

  const handleSpeedChange = () => {
    const speeds = [1, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    setPlaybackSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  };

  // Drag/scrub handlers
  const calculateTimeFromEvent = (clientX: number) => {
    if (!progressBarRef.current || !duration) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return percent * duration;
  };

  const handleProgressMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const newTime = calculateTimeFromEvent(e.clientX);
    setPlaybackTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleProgressTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    const touch = e.touches[0];
    const newTime = calculateTimeFromEvent(touch.clientX);
    setPlaybackTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  // Global drag event listeners
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newTime = calculateTimeFromEvent(e.clientX);
      setPlaybackTime(newTime);
      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const newTime = calculateTimeFromEvent(touch.clientX);
      setPlaybackTime(newTime);
      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
      }
    };

    const handleEnd = () => setIsDragging(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, duration]);

  // Style helpers
  const getButtonBgColor = () =>
    isUser ? "rgba(255,255,255,0.2)" : `${theme.primaryColor}20`;
  
  const getButtonColor = () =>
    isUser ? "#ffffff" : theme.primaryColor;
  
  const getTrackBgColor = () =>
    isUser ? "rgba(255,255,255,0.3)" : `${theme.borderColor}40`;
  
  const getProgressColor = () =>
    isUser ? "#ffffff" : theme.primaryColor;

  const progressPercent = duration ? (playbackTime / duration) * 100 : 0;

  return (
    <div className="flex flex-col gap-2 min-w-[200px]">
      {/* Controls Row */}
      <div className="flex items-center gap-3">
        {/* Play/Pause Button */}
        <motion.button
          onClick={handlePlayPause}
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: getButtonBgColor(),
            color: getButtonColor(),
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
        </motion.button>

        {/* Duration Display */}
        <div className="text-xs font-medium flex-1">
          {formatAudioTime(playbackTime)} / {formatAudioTime(duration)}
        </div>

        {/* Speed Control Button */}
        <motion.button
          onClick={handleSpeedChange}
          className="px-2 py-1 rounded-md text-xs font-bold flex-shrink-0"
          style={{
            backgroundColor: isUser ? "rgba(255,255,255,0.2)" : `${theme.primaryColor}15`,
            color: getButtonColor(),
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Change playback speed"
        >
          {playbackSpeed}x
        </motion.button>
      </div>

      {/* Progress Bar */}
      <div
        ref={progressBarRef}
        className="w-full h-4 rounded-full cursor-pointer relative group flex items-center select-none touch-none"
        onMouseDown={handleProgressMouseDown}
        onTouchStart={handleProgressTouchStart}
      >
        {/* Track Background */}
        <div
          className="absolute w-full h-1.5 rounded-full"
          style={{ backgroundColor: getTrackBgColor() }}
        />
        
        {/* Progress Fill */}
        <div
          className="absolute h-1.5 rounded-full pointer-events-none"
          style={{
            width: `${progressPercent}%`,
            backgroundColor: getProgressColor(),
          }}
        />
        
        {/* Draggable Handle */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full shadow-md transition-opacity ${
            isDragging ? "opacity-100 scale-110" : "opacity-0 group-hover:opacity-100"
          }`}
          style={{
            left: `calc(${progressPercent}% - 7px)`,
            backgroundColor: getProgressColor(),
          }}
        />
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        crossOrigin="anonymous"
      >
        <source src={audioSource.src} type={audioSource.type} />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default AudioPlayer;
